import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { RootState } from "store/ducks";

import { formatDocument } from "utils/Document";

import attachmentIcon from "img/icon-anexo.svg";

import { ordersItemsModelReviewOrderLost } from "components/Table/Orders/models";
import { FileTypesAccept } from "pages/OpenProtocol/interface";
import FloatingLabel from "components/FloatingLabel";
import { Column, Row } from "components/Layout/Grid";
import { Visibility } from "components/Visibility";
import RadioInput from "components/RadioInput";
import Validator from "components/Validator";
import TextArea from "components/TextArea";
import { Button } from "components/Button";
import Loading from "components/Loading";
import Table from "components/Table";
import Form from "components/Form";
import Text from "components/Text";

import { InputThemer } from "components/OpenProtocolOrderItems/styles";
import TicketServices from "services/ticket.service";
import { FileTypes, FormLessReviewProps, ItemDetails, PayloadLessReview, TicketReviewOrderLostResponse } from "./interface";
import { IUser } from "models/User";
import UploadService from "services/upload.service";
import { useTicketsContext } from "contexts/Tickets";

const ReviewOrderLost: React.FC = () => {

  const user = useSelector((state: { user: IUser; }) => state.user);
  const ticketState = useSelector((state: RootState | undefined) => state?.slider?.state);
  const { reloadTableTicket } = useTicketsContext();

  const dispatch = useDispatch();

  const ticketId = ticketState?.ticket.id;

  const [ticketLossReview, setTicketLossReview] = useState<TicketReviewOrderLostResponse>();
  const [files, setFiles] = useState<FileTypes | null>(null);
  const [filesMap, setFilesMap] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [isLoadingPage, setLoadingPage] = useState<boolean>(true);
  const [isLoadingButton, setLoadingButton] = useState<boolean>(false);

  const [formLessReview, setFormLessReview] = useState<FormLessReviewProps>({
    internalComment: "",
    mustShippingCompanyPayBack: false,
    items: []
  });

  const ticketService = new TicketServices();
  const _uploadService = new UploadService();

  useEffect(() => {
    if (ticketId) {
      fetchLossReview();
    }
  }, [ticketId]);

  useEffect(() => {
    updateFileDisplay();
  }, [files, ticketState?.ticket?.attachments]);

  const fetchLossReview = async () => {
    try {
      const response = await ticketService.getTicketLossReview(ticketId);
      if (response.data) {
        const options = response.data;
        setTicketLossReview(options);
        setFormLessReview({
          internalComment: options.internalComment,
          items: options.ticket.order.items,
          mustShippingCompanyPayBack: options.mustShippingCompanyPayBack
        });

        if (options?.incidentReport?.url) {
          const createFileObject = (name: string, url: string): FileTypes => {
            const blob = new Blob([""], { type: "application/octet-stream" });
            const file = new File([blob], name, { type: blob.type, lastModified: Date.now() });

            return Object.assign(file, { url });
          };

          const fileObj = createFileObject(options.incidentReport.name, options.incidentReport.url);
          setFiles(fileObj);
        }
        setLoadingPage(false);
      }
    } catch (error) {
      console.error('Erro ao buscar revisão do ticket', error);
      setLoadingPage(false);
    }
  };

  const handleFileUpload = async (file: FileTypes): Promise<FileTypes> => {
    if (FileTypesAccept.includes(file.type)) {
      return _uploadService.upload(file);
    }
    throw new Error("Invalid file type");
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target as HTMLSelectElement;
    const valueField = value === "true";

    setFormLessReview(prevState => ({
      ...prevState,
      mustShippingCompanyPayBack: valueField
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { type, value, dataset, name } = e.target as HTMLInputElement;
    const valueField: string | number | boolean = type === 'radio' ? !formLessReview.mustShippingCompanyPayBack : value;
    const stateName = dataset.statename || name;

    setFormLessReview(prevState => ({
      ...prevState,
      [stateName]: valueField
    }));
  };

  const handleItemQuantityChange = (e: React.FocusEvent<HTMLInputElement>, item: ItemDetails) => {
    const pastValue = item.lossQuantity ?? 0;
    const value = isNaN(Number(e.target.value)) ? pastValue : Number(e.target.value);

    if (value < 0) return;

    const fixedValue = value > item.maxQuantity ? item.maxQuantity : value;

    if (!item.number) {
      setErrorMessage("Número do item não encontrado.");
      return;
    }

    const itemIndex: number = formLessReview?.items
      .map(data => data.number)
      .indexOf(item.number) || 0;

    if (itemIndex > -1) {
      const updatedItems = formLessReview?.items.map((data, index) => {
        if (index === itemIndex) {
          return { ...data, lossQuantity: fixedValue };
        }
        return data;
      });

      setFormLessReview((prevState: FormLessReviewProps) => ({
        ...prevState,
        items: updatedItems
      }));

      e.target.value = `${fixedValue}`;
    }
  };

  async function addFile(e: React.ChangeEvent<HTMLInputElement>) {
    const fileTypes: Array<string> = FileTypesAccept;

    if (e.target.files && e.target.files.length > 0) {
      const arrayFiles: any = e.target.files[0];

      if (fileTypes.includes(arrayFiles.type)) {
        setFiles(arrayFiles);
      }
    }

    e.target.value = "";
  }

  function removeFile() {
    setFiles(null);
  }

  const updateFileDisplay = () => {
    const newFilesMap = files ? [
      <FloatingLabel
        key="fileDisplay"
        label={files.name}
        onRemove={removeFile}
        uri={files.url}
      />
    ] : [];

    setFilesMap(newFilesMap);
  };

  const handleSubmit = async () => {

    const isValid = validateFields();
    if (!isValid) return;

    setLoadingButton(true);

    try {

      let uploadedFileData = files ?? undefined;

      if (files && !files.url) {
        uploadedFileData = await handleFileUpload(files);
      }

      const payload = preparePayload(user, formLessReview, uploadedFileData);
      const response = await ticketService.putTicketLossReview(payload);
      if (response.status !== 200) {
        handleApiError(response);
      } else {
        dispatch({ type: 'CLOSE_SLIDER' });
        reloadTableTicket();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const validateFields = () => {
    if (formLessReview.mustShippingCompanyPayBack === undefined) {
      setErrorMessage("É obrigatório especificar se a transportadora deverá ressarcir o valor do extravio.");
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const handleApiError = (error: any) => {
    const defaultMsg = "Erro ao registrar revisão de extravio.";
    const errorMsg = error.response?.data?.data || error.data?.data || defaultMsg;
    console.error(defaultMsg, error);
    setErrorMessage(errorMsg);
    setLoadingButton(false);
  };

  const preparePayload = (user: IUser, form: FormLessReviewProps, fileData?: any) => {
    const payload: PayloadLessReview = {
      ticketId: ticketId,
      mustShippingCompanyPayBack: form.mustShippingCompanyPayBack,
      internalComment: form.internalComment,
      items: form.items.map(item => ({
        number: item.number,
        code: item.code,
        name: item.name,
        unit: item.unit,
        quantity: item.quantity,
        price: item.price || 0,
        returnQuantity: item.returnQuantity || 0,
        lossQuantity: item.lossQuantity,
        maxQuantity: item.maxQuantity || 0,
        lots: item.lots || []
      })),
      attendant: `${user?.username} - ${user?.fullName}`
    };

    if (fileData) {
      payload.incidentReport = {
        name: fileData.name,
        url: fileData.uri || fileData.fileUrl || fileData.url,
      };
    }

    return payload;
  };

  const classNameValue = `font-bold text-wrap pb-1`;

  return (
    <Column className="animated fadeIn px-0 bg-white">
      <Visibility visible={isLoadingPage}>
        <Row className="align-center justify-center mt-10">
          <Loading onlySpinner size={50} />
        </Row>
      </Visibility>

      <Visibility visible={!isLoadingPage}>
        <InputThemer>
          <Row
            className="align-center flex-md-column align-md-none flex-wrap justify-md-none bg-white py-3 grid-gap-2"
            style={{ borderRadius: "10px 10px 0px 0px" }}
          >
            <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
              <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
                Protocolo
              </Text>
              <Row className="flex-1 align-center grid-gap-3 flex-wrap">
                <Row className="flex-1 align-center">
                  <Text
                    className="mr-2 mb-1 color-blue-gray"
                    size={14}
                    weight="bold"
                  >
                    {ticketLossReview?.ticket?.protocolNumber}
                  </Text>
                </Row>
              </Row>
            </Column>
            <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
              <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
                CNPJ
              </Text>
              <Row className="flex-1 align-center grid-gap-3 flex-wrap">
                <Row className="flex-1 align-center">
                  <Text
                    className="mr-2 mb-1 color-blue-gray"
                    size={14}
                    weight="bold"
                  >
                    {formatDocument(ticketLossReview?.ticket?.cnpj ?? "")}
                  </Text>
                </Row>
              </Row>
            </Column>
            <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
              <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
                Cliente
              </Text>
              <Row className="flex-1 align-center grid-gap-3 flex-wrap">
                <Row className="flex-1 align-center">
                  <Text
                    className="mr-2 mb-1 color-blue-gray"
                    size={14}
                    weight="bold"
                  >
                    {ticketLossReview?.ticket?.customerName}
                  </Text>
                </Row>
              </Row>
            </Column>
          </Row>

          <Text size={12} className={classNameValue}>Itens do Pedido</Text>
          <Form
            id="form-loss-review"
            onValidate={() => true}
            customFunction={() => { }}
            disableEnterSubmit={true}
          >
            <Validator
              value={(formLessReview?.items?.length ?? 0) > 0}
              name="Itens do pedido"
              required
            >
              <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                <Table
                  trackingKey="Revisar processo de extravio#Itens do pedido"
                  model={[
                    ...ordersItemsModelReviewOrderLost,
                    {
                      label: "Qtde. Extraviada",
                      key: "lossQuantity",
                      Component: (data: ItemDetails) => (
                        <>
                          <input
                            id={`item-${data.number}`}
                            className="form-control form-control-sm bg-white"
                            onBlur={(e) => handleItemQuantityChange(e, data)}
                            defaultValue={data.lossQuantity}
                            min={0}
                            max={data.maxQuantity}
                          />
                        </>
                      ),
                      size: 90,
                      maxSize: 90,
                      noFilter: true
                    },
                  ]}
                  data={formLessReview?.items}
                  completeBlankSpaces={false}
                  rowHeight={40}
                  className="table-scroll w-100"
                />
              </div>
            </Validator>
            <Column className="mt-5">
              <Row className="align-center">
                <img src={attachmentIcon} alt="" />
                <Text className="ml-1 color-blue-gray" size={12}>
                  Anexar boletim de ocorrência
                </Text>
                <input
                  onChange={addFile}
                  id="open_protocol_files"
                  className="d-none"
                  type="file"
                  accept={FileTypesAccept.toString()}
                />
              </Row>
              <Row className="mt-1 align-center align-sm-none flex-sm-column  ">
                <label
                  className="button outline"
                  htmlFor="open_protocol_files"
                  id="select-file"
                >
                  <Text className="px-2 color-primary font-bold">
                    Selecionar Arquivo
                  </Text>
                </label>
                <Row className="ml-2 ml-sm-0 my-sm-2 align-center flex-wrap word-break">
                  {filesMap}
                </Row>
              </Row>
            </Column>

            <Column className="mt-5">
              <Text className="mb-2 color-blue-gray" size={12}>
                Comentário interno
              </Text>
              <TextArea
                stateName="internalComment"
                id="internalComment"
                minLength={7}
                name="Comentário interno"
                placeholder="Mensagem"
                defaultValue={formLessReview?.internalComment}
                onChange={handleInputChange}
                required
                style={{ minHeight: 150 }}
              />
            </Column>
            <Column className="mt-3">
              <Row className="flex-md-column align-center">
                <Row className="w-md-100 mt-md-2">
                  <Text
                    className="mb-2 mt-2 mr-4 color-blue-gray"
                    size={14}
                    weight={"bold"}
                  >
                    A transportadora deverá ressarcir o valor do extravio *
                  </Text>
                </Row>
                <Row className="w-md-100 mt-md-2 mr-4 ">
                  <RadioInput
                    id="mustShippingCompanyPayBack-Yes"
                    checked={formLessReview?.mustShippingCompanyPayBack === true}
                    onClick={handleSelectChange}
                    name="mustShippingCompanyPayBack"
                    defaultValue={true}
                    required
                  >
                    <Text className="color-blue-gray">Sim</Text>
                  </RadioInput>
                </Row>
                <Row className="w-md-100 mt-md-2">
                  <RadioInput
                    id="mustShippingCompanyPayBack-No"
                    checked={formLessReview?.mustShippingCompanyPayBack === false}
                    onClick={handleSelectChange}
                    name="mustShippingCompanyPayBack"
                    defaultValue={false}
                    required
                  >
                    <Text className="color-blue-gray">Não</Text>
                  </RadioInput>
                </Row>
              </Row>
            </Column>

            {errorMessage && (
              <Text className="color-danger mt-2 animated fadeIn" size={14}>
                {errorMessage}
              </Text>
            )}

            <Column className="mt-1">
              <Row className="my-3 flex-1 justify-center align-center">
                <Button
                  className="button outline"
                  width="auto"
                  id="register-loss-review"
                  onClick={handleSubmit}
                  isRequesting={isLoadingButton}
                  spinnerColor="#0E4C54"
                >
                  <Text className="color-primary font-bold px-3">
                    Registrar extravio
                  </Text>
                </Button>
              </Row>
            </Column>
          </Form>
        </InputThemer>
      </Visibility>
    </Column>
  );
};

export default ReviewOrderLost;
